import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, AGGREGATION_TYPES } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

export function GaugeConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  const aggregatableFields = useMemo(() => {
    if (!config.dataSource) return []

    if (
      config.dataSource === 'custom_query' &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      return fieldsFromCustomQuery(config.customQuery)
        .filter((f) => f.aggregatable)
        .map((f) => ({
          name: f.name,
          label: f.label,
          aggregatable: true as const,
        }))
    }

    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return dynamicSource.fields.filter(f => f.aggregatable)
    }

    return getAggregatableFields(config.dataSource)
  }, [dataSources, config.dataSource, cqSig])
  const gaugeConfig = config.gaugeConfig || { valueKey: '', min: 0, max: 100, thresholds: [
    { value: 33, color: '#ef4444', label: 'Bajo' },
    { value: 66, color: '#f59e0b', label: 'Medio' },
    { value: 100, color: '#10b981', label: 'Alto' }
  ]}

  const isValid = Boolean(
    config.metadata?.title &&
    gaugeConfig.valueKey &&
    config.aggregation
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Medidor/Gauge</h2>
      <p className="text-gray-600 mb-6">
        Indicador visual con umbrales (bajo/medio/alto)
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título del Indicador <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Índice de Sostenibilidad Forestal"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Campo Numérico */}
        <div>
          <label className="block font-medium mb-2">
            Campo Numérico <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={gaugeConfig.valueKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                gaugeConfig: { ...gaugeConfig, valueKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
        </div>

        {/* Agregación */}
        <div>
          <label className="block font-medium mb-2">
            Operación <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-3">
            {AGGREGATION_TYPES.map((agg) => (
              <button
                key={agg.value}
                type="button"
                className={`
                  border-2 rounded p-3 text-center transition-all
                  ${
                    config.aggregation === agg.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() => onChange({ ...config, aggregation: agg.value as any })}
              >
                <div className="text-xl mb-1">{agg.icon}</div>
                <div className="text-xs font-medium">{agg.value}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Rango Min-Max */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-2">Valor Mínimo</label>
            <input
              type="number"
              placeholder="0"
              className="w-full border rounded px-4 py-2"
              value={gaugeConfig.min ?? ''}
              onChange={(e) =>
                onChange({
                  ...config,
                  gaugeConfig: { ...gaugeConfig, min: parseFloat(e.target.value) || 0 },
                })
              }
            />
          </div>
          <div>
            <label className="block font-medium mb-2">Valor Máximo</label>
            <input
              type="number"
              placeholder="100"
              className="w-full border rounded px-4 py-2"
              value={gaugeConfig.max ?? ''}
              onChange={(e) =>
                onChange({
                  ...config,
                  gaugeConfig: { ...gaugeConfig, max: parseFloat(e.target.value) || 100 },
                })
              }
            />
          </div>
        </div>

        {/* Unidad */}
        <div>
          <label className="block font-medium mb-2">Unidad de Medida</label>
          <input
            type="text"
            placeholder="Ej: %, puntos, índice"
            className="w-full border rounded px-4 py-2"
            value={gaugeConfig.unit || ''}
            onChange={(e) =>
              onChange({
                ...config,
                gaugeConfig: { ...gaugeConfig, unit: e.target.value },
              })
            }
          />
        </div>

        {/* Información de umbrales */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-900 mb-2">🎯 Umbrales Predefinidos</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></div>
              <span>0-33%: Bajo (Rojo)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
              <span>34-66%: Medio (Naranja)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
              <span>67-100%: Alto (Verde)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}
